import { AppType } from "client/Api/Apps/common";
import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { ZaaksysteemLiveTileContent } from "./ZaaksysteemLiveTileContent";
import { navigateToZaaksysteemApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { ZaaksysteemLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Zaaksysteem/provider";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type ZaaksysteemTileContentProps = {
	renderState: TileRenderState<AppType.Zaaksysteem>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	provider: ZaaksysteemLiveInformationProvider;
};

export const ZaaksysteemTileContent = (props: ZaaksysteemTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		provider
	} = props;

	const [navigationElement, navigateHandler] = useZaaksysteemTileNavigation(renderState, onEdit);

	const {
		dimension,
		data: { settings }
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<ZaaksysteemLiveTileContent
				tabs={settings.tabs}
				provider={provider}
				destinationWindow={settings.destinationWindow}
				connectionId={settings.connectionId}
				onNavigate={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
ZaaksysteemTileContent.displayName = "ZaaksysteemTileContent";

export function useZaaksysteemTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Zaaksysteem>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		environmentAppId,
		settings: {
			destinationWindow
		}
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				registerTileUsage();

				navigateToZaaksysteemApp({
					id: environmentAppId,
					destinationWindow
				});
			},
			[
				destinationWindow,
				environmentAppId,
				registerTileUsage
			]
		)
	);
}
